import { get as baseGet, set as baseSet, remove as baseRemove } from "local-storage"

const SESSION_CONFIG_KEY = "Consumer_sessionConfig"
const REMEMBER_MAIL_KEY = "Consumer_rememberMail"
export const USER_OPERATOR_KEY = "Consumer_userOperator"

type StorageKey = string
type StorageValue = string
type StorageOptions = {
	target?: string
	priority?: string
}

const localStorageService = {
	get(key: StorageKey, defaultValue: StorageValue = null): StorageValue {
		const value: StorageValue = baseGet(key)
		return value === null ? defaultValue : value
	},

	set(key: StorageKey, value: StorageValue): void {
		baseSet(key, value)
	},

	remove(key: StorageKey): void {
		baseRemove(key)
	},

	clearSession(): void {
		this.remove(SESSION_CONFIG_KEY)
	},
}

const sessionStorageService = {
	get(key: StorageKey, defaultValue: StorageValue = null): StorageValue {
		const value = sessionStorage.getItem(key)

		return value === null ? defaultValue : value
	},

	set(key: StorageKey, value: StorageValue): void {
		return sessionStorage.setItem(key, value)
	},

	remove(key: StorageKey): void {
		sessionStorage.removeItem(key)
	},

	clearSession(): void {
		sessionStorage.clear()
	},
}

const browserStorageService = {
	defaultTarget: "localStorage",

	// NB: in this class the methods have an optional "priority" param.
	// By default the used storage is the localstorage.
	// The priority param allows us to first search if the key exists in another
	// given storage (mainly the session storage). If so, the given storage is used.

	get(key: StorageKey, options: StorageOptions = {}): StorageValue {
		const target = options?.target ?? this.defaultTarget
		const priority = options?.priority

		const localStorageKey = localStorageService.get(key)
		const sessionStorageKey = sessionStorageService.get(key)

		if (target === "sessionStorage") {
			if (priority === "localStorage") {
				return localStorageKey ?? sessionStorageKey
			} else {
				return sessionStorageKey
			}
		} else if (target === "localStorage") {
			if (priority === "sessionStorage") {
				return sessionStorageKey ?? localStorageKey
			} else {
				return localStorageKey
			}
		}
	},

	set(key: StorageKey, value: StorageValue, options: StorageOptions = {}): void {
		const target = options?.target ?? this.defaultTarget
		const priority = options?.priority

		const localStorageKey = localStorageService.get(key)
		const sessionStorageKey = sessionStorageService.get(key)
		if (target === "sessionStorage") {
			// if priority is given to localStorage and the key exist in the storage
			if (priority === "localStorage" && localStorageKey) {
				return localStorageService.set(key, value)
			} else {
				return sessionStorageService.set(key, value)
			}
		} else if (target === "localStorage") {
			// if priority is given to sessionStorage and the key exist in the storage
			if (priority === "sessionStorage" && sessionStorageKey) {
				return sessionStorageService.set(key, value)
			} else {
				return localStorageService.set(key, value)
			}
		}
	},

	remove(key: StorageKey, options: StorageOptions = {}): void {
		const target = options?.target ?? this.defaultTarget
		const priority = options?.priority

		const localStorageKey = localStorageService.get(key)
		const sessionStorageKey = sessionStorageService.get(key)
		if (target === "sessionStorage") {
			// if priority is given to localStorage and the key exist in the storage
			if (priority === "localStorage" && localStorageKey) {
				return localStorageService.remove(key)
			} else {
				return sessionStorageService.remove(key)
			}
		} else if (target === "localStorage") {
			// if priority is given to sessionStorage and the key exist in the storage
			if (priority === "sessionStorage" && sessionStorageKey) {
				return sessionStorageService.remove(key)
			} else {
				return localStorageService.remove(key)
			}
		}
	},
}

export {
	browserStorageService as default,
	localStorageService,
	sessionStorageService,
	REMEMBER_MAIL_KEY,
	SESSION_CONFIG_KEY,
}
